/**
 * ملف المعلم — `/teachers/{id}` (design/TeacherFile.dc.html).
 *
 * ⚠️ **الفرق بين التصميم والمبني معلَن مش متبلّع.** التصميم فيه أربع بطاقات
 *    إحصاء (طلابه · ساعات الشهر · غيابات الشهر · تقارير متأخرة) وأقسام
 *    (مخالفات ومكافآت · إنذارات · طلبات تغيير المعلم · فيدباك أولياء الأمور).
 *    السيستم لسه بيرجّع **الطلاب والاشتراكات وبس** — مفيش مسار للمخالفات ولا
 *    للإنذارات ولا للفيدباك.
 *    ⛔ فبدل بطاقات فاضية بأصفار مخترعة، الشاشة بتعرض اللي موجود فعلًا
 *    وبتكتب صراحة إيه اللي لسه ما وصلش. الصفر الكاذب أسوأ من النقص المعلن.
 */
import { useCallback, useEffect, useState } from "react";
import { Shell } from "@/components/Shell";
import { ErrorState, LoadingState, OfflineState } from "@/components/states/ScreenStates";
import {
  Avatar,
  Dash,
  DateAr,
  DefRow,
  HiddenSection,
  Maybe,
  NeedsReviewMark,
  Section,
} from "@/components/RegistryBits";
import { Ltr, fmtNum } from "@/lib/format";
import { ApiError, errorMessageAr, OfflineError } from "@/api/client";
import { useSession } from "@/lib/session";
import { Link, useRouter } from "@/lib/router";
import {
  archiveTeacher,
  fetchTeacher,
  fetchTeachers,
  mergeTeacherInto,
  latinDigits,
  MANUAL_TEACHER_STATUSES,
  setTeacherStatus,
  studentTone,
  teacherTone,
  type TeacherDetail,
} from "@/api/registry";
import "@/styles/registry.css";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; teacher: TeacherDetail }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

export function TeacherFile({ teacherId }: { teacherId: string }) {
  const { navigate } = useRouter();
  const { me } = useSession();
  const canWrite = (me?.permissions ?? []).includes("teacher.write") && me?.user.role !== "observer";
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      setState({ kind: "ready", teacher: await fetchTeacher(teacherId) });
    } catch (err) {
      console.error(`[teacher] تحميل ملف المعلم ${teacherId} فشل`, err);
      setState(
        err instanceof OfflineError
          ? { kind: "offline" }
          : { kind: "error", messageAr: errorMessageAr(err) },
      );
    }
  }, [teacherId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") {
    return (
      <Shell title="ملف المعلم">
        <LoadingState rows={5} />
      </Shell>
    );
  }
  if (state.kind === "offline") {
    return (
      <Shell title="ملف المعلم">
        <OfflineState onRetry={() => void load()} />
      </Shell>
    );
  }
  if (state.kind === "error") {
    return (
      <Shell title="ملف المعلم">
        <ErrorState messageAr={state.messageAr} onRetry={() => void load()} />
        <Link to="/teachers" className="link">
          ‹ رجوع لسجل المعلمين
        </Link>
      </Shell>
    );
  }

  const t = state.teacher;
  const activeEnrollments = t.enrollments.filter((e) => e.status === "active").length;

  return (
    <Shell
      title="ملف المعلم"
      crumb={t.teacher_code ? <Ltr>{t.teacher_code}</Ltr> : "بلا كود"}
    >
      {/* ═══ (1) شريط الرجوع ═══ */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Link to="/teachers" className="link">
          ‹ رجوع لسجل المعلمين
        </Link>
        <button type="button" className="link" onClick={() => void load()}>
          تحديث الملف
        </button>
        {toast ? (
          <span className="chip okc" role="status">
            <span className="bullet" />
            {toast}
          </span>
        ) : null}
      </div>

      {/* ═══ (2) بطاقة الهيدر ═══ */}
      <div className="card" style={{ overflow: "hidden" }}>
        <div className="accent-bar" />
        <div
          style={{ padding: "18px 20px 20px", display: "flex", flexDirection: "column", gap: 12 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <Avatar name={t.name_ar} size={48} />
            <div
              className="clip"
              style={{ fontSize: 20, fontWeight: 600, color: "var(--blue-900)" }}
            >
              {t.name_ar || <span className="missing">بلا اسم مسجّل</span>}
            </div>
            {t.status_label_ar ? (
              <span className={`chip ${teacherTone(t.status)}`}>
                <span className="bullet" />
                {latinDigits(t.status_label_ar)}
              </span>
            ) : null}
            <span style={{ marginRight: "auto" }}>
              <NeedsReviewMark q={t.data_quality} />
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {t.subjects.length > 0 ? (
              <span className="chip ghost">{t.subjects.join(" · ")}</span>
            ) : (
              <span className="chip ghost">المواد مش مسجّلة</span>
            )}
            {t.languages_ar.length > 0 ? (
              <span className="chip ghost">{t.languages_ar.join(" · ")}</span>
            ) : null}
            {t.performance_ar ? (
              // ⚠️ تقدير أداء مش حالة توظيف — معروض منفصل عن الحالة عمدًا
              <span className="chip ghost">التقدير: {t.performance_ar}</span>
            ) : null}
            {!t.supervisor && t.supervisor_name_raw ? (
              <span className="chip ghost">المشرفة: {t.supervisor_name_raw} (لسه بلا حساب)</span>
            ) : null}
            {t.supervisor ? (
              <span className="chip ghost">المشرفة: {t.supervisor.name_ar}</span>
            ) : (
              <span className="chip ghost">مفيش مشرفة متسندة</span>
            )}
            <span className={`chip ${t.telegram_linked ? "okc" : "ghost"}`}>
              <span className="bullet" />
              {t.telegram_linked ? "تليجرام مربوط" : "تليجرام مش مربوط"}
            </span>
          </div>
        </div>
      </div>

      {/* ═══ (3) الإحصاء — اللي السيستم بيرجّعه فعلًا ═══ */}
      <div className="stats c3">
        <div className="stat">
          <div className="v">
            {/* ⛔ `null` = محجوب، مش صفر */}
            {t.students_count === null ? (
              <span className="missing" style={{ fontSize: 16 }}>
                محجوب
              </span>
            ) : (
              <span className="num">{fmtNum(t.students_count)}</span>
            )}
          </div>
          <div className="l">طلابه</div>
        </div>
        <div className="stat">
          <div className="v">
            {t.enrollments_visible ? (
              <span className="num">{fmtNum(activeEnrollments)}</span>
            ) : (
              <span className="missing" style={{ fontSize: 16 }}>
                محجوب
              </span>
            )}
          </div>
          <div className="l">اشتراكات نشطة</div>
        </div>
        <div className="stat">
          <div className="v">
            <span className="num">{fmtNum(t.data_quality.needs_review ? t.data_quality.issues.length : 0)}</span>
          </div>
          <div className="l">خانات ناقصة</div>
        </div>
      </div>
      <div className="t-meta">
        ساعات الشهر وغيابات الشهر والتقارير المتأخرة — والمخالفات والمكافآت والإنذارات وطلبات
        تغيير المعلم وفيدباك أولياء الأمور — لسه مش بترجع من السيستم، فمش معروضة هنا بدل ما
        تتعرض بأصفار مش حقيقية.
      </div>

      {/* ═══ (4) العمودان ═══ */}
      <div className="reg-cols">
        <div className="reg-col">
          <Section
            title="طلابه"
            note={
              t.students_visible ? (
                <>
                  <span className="num">{fmtNum(t.students.length)}</span> طالب — مميّزين مش اشتراكات
                </>
              ) : undefined
            }
          >
            {!t.students_visible ? (
              <HiddenSection what="قايمة طلابه" />
            ) : t.students.length === 0 ? (
              <div className="t-desc">مفيش طلاب على المعلم ده دلوقتي.</div>
            ) : (
              <div className="reg-scroll" style={{ margin: "0 -20px" }}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>الطالب</th>
                      <th>الكود</th>
                      <th>الحالة</th>
                      {t.students_roster_only ? null : <th>إجراء</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {t.students.map((st) => (
                      <tr key={st.id} className="row-link">
                        <td className="t-body clip">
                          {st.name_ar || <span className="missing">بلا اسم مسجّل</span>}
                        </td>
                        <td className="t-desc">
                          {st.student_code ? <Ltr>{st.student_code}</Ltr> : <Dash />}
                        </td>
                        <td>
                          {st.status_label_ar ? (
                            <span className={`chip ${studentTone(st.status)}`}>
                              <span className="bullet" />
                              {latinDigits(st.status_label_ar)}
                            </span>
                          ) : (
                            <Dash />
                          )}
                        </td>
                        {/* ⛔ قسم بيشوف الأسماء بس ما يقدرش يفتح السجل — زرار
                            بيوقّع على «مالكش صلاحية» أسوأ من غيابه */}
                        {t.students_roster_only ? null : (
                          <td>
                            <button
                              type="button"
                              className="btn sm ghost"
                              onClick={() => navigate(`/students/${st.id}`)}
                            >
                              فتح السجل
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section title="اشتراكاته النشطة">
            {!t.enrollments_visible ? (
              <HiddenSection what="الاشتراكات" />
            ) : t.enrollments.length === 0 ? (
              <div className="t-desc">مفيش اشتراكات نشطة على المعلم ده.</div>
            ) : (
              <div className="reg-scroll" style={{ margin: "0 -20px" }}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>الطالب</th>
                      <th>المادة</th>
                      <th>الحالة</th>
                      <th>بدأ</th>
                      <th>المتبقي</th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.enrollments.map((e) => (
                      <tr key={e.id}>
                        <td className="t-body clip">
                          {e.student ? e.student.name_ar : <span className="missing">الطالب مش موجود</span>}
                        </td>
                        <td className="t-desc">
                          <Maybe v={e.subject} />
                        </td>
                        <td>
                          {e.status_label_ar ? (
                            <span className={`chip ${e.status === "active" ? "okc" : "ghost"}`}>
                              <span className="bullet" />
                              {latinDigits(e.status_label_ar)}
                            </span>
                          ) : (
                            <Dash />
                          )}
                        </td>
                        <td className="t-desc">
                          <DateAr v={e.started_at} />
                        </td>
                        <td className="t-desc">
                          {/* ⛔ الساعات نص زي ما رجعت — بلا تقريب */}
                          {e.hours_balance ? (
                            <>
                              <Ltr>{e.hours_balance}</Ltr> ساعة
                            </>
                          ) : (
                            <Dash />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>

        <div className="reg-col">
          <Section title="بيانات المعلم">
            <div className="deflist">
              <DefRow k="الكود">
                {t.teacher_code ? <Ltr>{t.teacher_code}</Ltr> : <Dash />}
              </DefRow>
              <DefRow k="الاسم بالإنجليزي">
                {t.name_en ? <Ltr>{t.name_en}</Ltr> : <Dash />}
              </DefRow>
              <DefRow k="الرقم">
                {t.phone_masked ? <Ltr>{t.phone_masked}</Ltr> : <span className="missing">مفيش رقم مسجّل</span>}
              </DefRow>
              <DefRow k="البريد">{t.email ? <Ltr>{t.email}</Ltr> : <Dash />}</DefRow>
              <DefRow k="انضم في">
                <DateAr v={t.joined_at} />
              </DefRow>
              <DefRow k="نسخة السجل">
                <span className="num">{fmtNum(t.version)}</span>
              </DefRow>
            </div>
          </Section>

          <Section title="جودة السجل" action={<NeedsReviewMark q={t.data_quality} />}>
            {!t.data_quality.needs_review ? (
              <span className="chip okc">
                <span className="bullet" />
                مكتمل
              </span>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {t.data_quality.issues_ar.length === 0 ? (
                  <span className="t-desc">السجل موسوم للمراجعة والسبب مش مكتوب.</span>
                ) : (
                  t.data_quality.issues_ar.map((issue, i) => (
                    <span className="t-desc" key={`${issue}-${i}`}>
                      · {issue}
                    </span>
                  ))
                )}
                {/* ⚠️ سجل المعلم مالوش `PATCH` في الباك إند — الاستكمال في
                    مكانه متاح للطالب والأسرة بس، والحقيقة دي مكتوبة مش مخبّية. */}
                <span className="t-meta">
                  استكمال سجل المعلم لسه مالوش مسار في السيستم — الاستكمال متاح لسجل الطالب
                  والأسرة.
                </span>
              </div>
            )}
          </Section>

          {/* 🔑 كل اللي بيغيّر السجل في قسم واحد في الآخر — الحالة فوق،
              والمسح تحتها مطوي. مش بلوكين وسط البيانات. */}
          <Section title="إدارة السجل">
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <TeacherStatusPanel
                teacher={t}
                canChange={canWrite}
                onSaved={(msg) => {
                  setToast(msg);
                  void load();
                }}
              />
              {canWrite ? (
                <div style={{ borderTop: "1px solid var(--line)", paddingTop: 12 }}>
                  <ArchiveTeacherPanel
                    teacher={t}
                    onDone={(targetId) => navigate(targetId ? `/teachers/${targetId}` : "/teachers")}
                  />
                </div>
              ) : null}
            </div>
          </Section>
        </div>
      </div>
    </Shell>
  );
}

const fieldStyle = {
  font: "inherit",
  color: "var(--ink)",
  background: "var(--surface)",
  border: "1px solid var(--line)",
  borderRadius: "var(--r-control)",
  padding: "8px 10px",
} as const;

/**
 * حالة المعلم — نفس عقد لوحة حالة الطالب: سبب مكتوب + نسخة السجل.
 * طلب الإشراف الفني (٦ أكتوبر): «معلمين كتير متوقفين» وسجلهم لسه «نشط» من الاستيراد.
 */
function TeacherStatusPanel({
  teacher,
  canChange,
  onSaved,
}: {
  teacher: TeacherDetail;
  canChange: boolean;
  onSaved: (toastAr: string) => void;
}) {
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const options = MANUAL_TEACHER_STATUSES.filter((o) => o.value !== teacher.status);
  const ready = status !== "" && reason.trim().length >= 3;

  async function save() {
    if (busy) return;
    // 🔴 الزرار المقفول بلا كلام = «السيستم رفض» عند الموظفة (حصل فعلًا).
    //    الزرار مفتوح دايمًا، والناقص بيتقال بالاسم عند الضغط.
    if (status === "") {
      setError("اختار الحالة الجديدة الأول من القايمة.");
      return;
    }
    if (reason.trim().length < 3) {
      setError("اكتب سبب التغيير في خانة السبب الأول — مثلًا: وقف من أول الشهر.");
      return;
    }
    setBusy(true);
    setError(null);
    setStale(false);
    try {
      const out = await setTeacherStatus(teacher.id, teacher.version, {
        status,
        reason_ar: reason.trim(),
      });
      setStatus("");
      setReason("");
      onSaved(out.toastAr);
    } catch (err) {
      console.error("[teacher] تغيير حالة المعلم فشل", err);
      setError(errorMessageAr(err));
      setStale(err instanceof ApiError && err.status === 409);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <span className="t-meta">حالة المعلم</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {teacher.status_label_ar ? (
            <span className={`chip ${teacherTone(teacher.status)}`}>
              <span className="bullet" />
              {latinDigits(teacher.status_label_ar)}
            </span>
          ) : (
            <Dash />
          )}
          <span className="t-meta">
            آخر تغيير: <DateAr v={teacher.status_changed_at} />
          </span>
        </div>
        <div className="t-body">
          {teacher.status_reason_ar ? (
            latinDigits(teacher.status_reason_ar)
          ) : (
            <span className="missing">مفيش سبب متسجّل للحالة دي</span>
          )}
        </div>

        {!canChange ? (
          <div className="t-desc">مالكش صلاحية تغيّر الحالة — العرض بس.</div>
        ) : (
          <>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="t-meta">الحالة الجديدة</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{ ...fieldStyle, width: "auto" }}
              >
                <option value="">— اختار الحالة —</option>
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label_ar}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="t-meta">السبب — بيتخزّن في السجل</span>
              <textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="مثال: وقف من أول الشهر — ظروف أسرية"
                style={{ ...fieldStyle, width: "100%", resize: "vertical" }}
              />
            </label>
            {error ? (
              <span className="t-meta" role="alert" style={{ color: "var(--danger)" }}>
                {error}
                {stale ? " — اضغط «تحديث الملف» فوق وشوف الحالة الجديدة الأول." : ""}
              </span>
            ) : null}
            <div className="fix-line">
              <span className="t-meta">
                {status === ""
                  ? "اختار الحالة الجديدة"
                  : reason.trim().length < 3
                    ? "اكتب السبب — بلا سبب مفيش تغيير"
                    : "جاهز للتغيير"}
              </span>
              <button
                type="button"
                className={`btn sm ${ready ? "primary" : "ghost"}${busy ? " is-busy" : ""}`}
                style={{ marginRight: "auto" }}
                disabled={busy}
                aria-busy={busy}
                onClick={() => void save()}
              >
                {busy ? "بيتغيّر…" : "تغيير الحالة"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** مسح سجل معلم مكرر — ناعم بسبب. السيستم بيرفض لو عنده طلاب نشطين. */
function ArchiveTeacherPanel({
  teacher,
  onDone,
}: {
  teacher: TeacherDetail;
  onDone: (targetId?: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** السجل عليه طلاب ⇒ المسح مرفوض (صح) — بنفتح مسار «انقل الطلاب للسجل الصح» */
  const [needsMerge, setNeedsMerge] = useState(false);
  const [targetQuery, setTargetQuery] = useState("");
  const [candidates, setCandidates] = useState<{ id: string; name_ar: string; teacher_code: string; students_count: number | null }[]>([]);
  const [target, setTarget] = useState<{ id: string; name_ar: string; teacher_code: string } | null>(null);
  const ready = reason.trim().length >= 3;

  useEffect(() => {
    if (!needsMerge) return;
    const q = targetQuery.trim();
    if (q.length < 2) {
      setCandidates([]);
      return;
    }
    const id = window.setTimeout(() => {
      void fetchTeachers({ segment: "all", page: 1, perPage: 8, q })
        .then((page) =>
          setCandidates(
            page.rows
              .filter((r) => r.id !== teacher.id)
              .map((r) => ({ id: r.id, name_ar: r.name_ar, teacher_code: r.teacher_code, students_count: r.students_count })),
          ),
        )
        .catch(() => setCandidates([]));
    }, 300);
    return () => window.clearTimeout(id);
  }, [needsMerge, targetQuery, teacher.id]);

  async function submit() {
    if (busy) return;
    if (!ready) {
      setError("اكتب سبب المسح في الخانة الأول — مثلًا: اسم مكرر، السجل الصح هو RA-T-00012.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await archiveTeacher(teacher.id, reason.trim());
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.code === "TEACHER_HAS_ACTIVE_STUDENTS") {
        // 🔴 بلاغ الإشراف الفني: «الاتنين فيهم طلاب وعايزة أمسح — أنقل الطلاب
        //    إزاي؟». الرفض صح، بس لازم يفتح الطريق مش يقفل الباب.
        setNeedsMerge(true);
        setError(null);
      } else {
        setError(errorMessageAr(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function merge() {
    if (busy) return;
    if (!target) {
      setError("اختار السجل الصح من نتايج البحث الأول.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const out = await mergeTeacherInto(teacher.id, target.id, reason.trim());
      console.info("[teacher] اتدمج", out.moved);
      onDone(target.id);
    } catch (err) {
      setError(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span className="t-meta">سجل مكرر؟</span>
        <button type="button" className="link" style={{ color: "var(--danger)" }} onClick={() => setOpen(true)}>
          مسح السجل ده
        </button>
      </div>
    );
  }
  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <span className="t-meta">مسح سجل المعلم</span>
        <span className="t-desc">
          السجل هيختفي من سجل المعلمين وما يرجعش. لو المعلم ده عنده طلاب نشطين،
          السيستم هيرفض — غيّر حالته بدل المسح.
        </span>
        <textarea
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="مثال: اسم مكرر — السجل الصح هو RA-T-00012"
          style={{ ...fieldStyle, width: "100%", resize: "vertical" }}
        />
        <span className="t-meta">
          {ready ? "جاهز — اضغط «مسح السجل»" : "اكتب السبب الأول، وبعدها اضغط «مسح السجل»"}
        </span>
        {needsMerge ? (
          <div
            style={{ display: "flex", flexDirection: "column", gap: 8, padding: 12,
                     background: "var(--blue-50)", borderRadius: "var(--r-control)" }}
          >
            <span className="t-body">السجل ده عليه طلاب، فمش هيتمسح وهم عليه.</span>
            <span className="t-desc">
              دوّر على سجل المعلم الصح واختاره — الطلاب والإجازات هتتنقل له، وبعدها السجل ده يتمسح لوحده.
            </span>
            <input
              type="text"
              value={targetQuery}
              onChange={(e) => {
                setTargetQuery(e.target.value);
                setTarget(null);
              }}
              placeholder="اكتب اسم المعلم أو كوده…"
              style={{ ...fieldStyle, width: "100%" }}
            />
            {candidates.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {candidates.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`btn sm ${target?.id === c.id ? "accent" : "ghost"}`}
                    style={{ justifyContent: "space-between" }}
                    aria-pressed={target?.id === c.id}
                    onClick={() => setTarget(c)}
                  >
                    <span>{c.name_ar}</span>
                    <span className="t-meta">
                      <Ltr>{c.teacher_code}</Ltr>
                      {c.students_count !== null ? ` · ${fmtNum(c.students_count)} طالب` : ""}
                    </span>
                  </button>
                ))}
              </div>
            ) : targetQuery.trim().length >= 2 ? (
              <span className="t-meta">مفيش معلم بالاسم ده.</span>
            ) : null}
          </div>
        ) : null}
        {error ? (
          <span className="t-meta" role="alert" style={{ color: "var(--danger)" }}>
            {error}
          </span>
        ) : null}
        <div className="fix-line">
          <button type="button" className="btn sm ghost" disabled={busy} onClick={() => { setOpen(false); setNeedsMerge(false); }}>
            إلغاء
          </button>
          {needsMerge ? (
            <button
              type="button"
              className={`btn sm ${target ? "primary" : "ghost"}${busy ? " is-busy" : ""}`}
              style={{ marginRight: "auto" }}
              disabled={busy}
              onClick={() => void merge()}
            >
              {busy ? "بيتنقل…" : target ? `انقل الطلاب لـ${target.name_ar} وامسح السجل ده` : "انقل الطلاب وامسح السجل ده"}
            </button>
          ) : (
            <button
              type="button"
              className={`btn sm danger${busy ? " is-busy" : ""}`}
              style={{ marginRight: "auto" }}
              disabled={busy}
              onClick={() => void submit()}
            >
              {busy ? "بيتمسح…" : "مسح السجل"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
